import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map, forkJoin } from 'rxjs';
import {
  ModalFileViewerComponent,
  FileType,
  ModalFileViewerData,
} from '../../../../shared/components/modal-file-viewer/modal-file-viewer.component';
import { ModalSpinnerComponent } from '../../../../shared/components/modal-spinner/modal-spinner.component';
import { TipoReporteResumenClinico } from '../../../../shared/enums';
import { ReportesService } from '../reportes.service';
import {
  SnackInfoComponent,
  SnackInfoProps,
  SnackInfoType,
} from '../../../../shared/components/snack-info/snack-info.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root',
})
export class ResumenClinicoService {
  private readonly _reportesService = inject(ReportesService);
  private readonly _dialog = inject(MatDialog);
  private readonly _snackBar = inject(MatSnackBar);

  public openResumenClinicoModal(idAgendamiento: string): void {
    const reportes = this.obtenerReportesResumenClinico(idAgendamiento);
    this.procesarReportes(reportes, idAgendamiento);
  }

  public openReportePorNombre(nombre: TipoReporteResumenClinico, idAgendamiento: string): void {
    const reporte = this.obtenerReportePorNombre(nombre, idAgendamiento);
    this.procesarReportes(reporte, idAgendamiento);
  }

  public openDocumentosConsulta(idAgendamiento: string): void {
    const documentos = this.obtenerReportesDocumentosConsulta(idAgendamiento);
    this.procesarReportes(documentos, idAgendamiento);
  }

  private procesarReportes(
    reportes: Array<{
      nombre: TipoReporteResumenClinico;
      blob: Observable<Blob>;
      order: number;
    }>,
    idAgendamiento: string
  ): void {
    const _spinner = this._dialog.open(ModalSpinnerComponent, {
      disableClose: true,
      panelClass: 'transparent-dialog',
    });

    const pdfBlobs = reportes.map((data) =>
      data.blob.pipe(map((blob) => ({ blob, name: data.nombre, order: data.order })))
    );

    forkJoin(pdfBlobs).subscribe({
      next: (pdfBlobs) => {
        _spinner.close();

        const orderedPdfBlobs = pdfBlobs.sort((a, b) => a.order - b.order);

        const modal = this._dialog.open(ModalFileViewerComponent, {
          panelClass: 'file-viewer-dialog',
          disableClose: true,
          autoFocus: false,
          data: {
            files: orderedPdfBlobs.map(({ blob, name }) => ({ blob, name })),
            type: FileType.PDF,
          } as ModalFileViewerData,
        });

        modal.componentInstance.sendAction$.subscribe((value) => {
          modal.componentInstance.isSendingAction.set(true);

          this._reportesService
            .enviarResumenClinicoPorNombre(value as TipoReporteResumenClinico, idAgendamiento)
            .subscribe({
              next: () => {
                modal.componentInstance.isSendingAction.set(false);

                this._snackBar.openFromComponent(SnackInfoComponent, {
                  data: {
                    message: 'Se ha enviado el correo electrónico con los documentos',
                    type: SnackInfoType.SUCCESS,
                  } as SnackInfoProps,
                });
              },
              error: () => {
                modal.componentInstance.isSendingAction.set(false);

                this._snackBar.openFromComponent(SnackInfoComponent, {
                  data: {
                    message:
                      'No pudimos enviar el correo electrónico con los documentos, inténtalo nuevamente',
                    type: SnackInfoType.ERROR,
                  } as SnackInfoProps,
                });
              },
            });
        });
      },
      error: (error) => {
        _spinner.close();
        console.error('Error al obtener los PDFs:', error);
      },
    });
  }

  private obtenerReportesResumenClinico(idAgendamiento: string): Array<{
    nombre: TipoReporteResumenClinico;
    blob: Observable<Blob>;
    order: number;
  }> {
    return this.configurarReportes(
      [
        { nombre: TipoReporteResumenClinico.RecetaMedica, order: 2 },
        { nombre: TipoReporteResumenClinico.InformeMedico, order: 1 },
        { nombre: TipoReporteResumenClinico.Terapias, order: 3 },
        { nombre: TipoReporteResumenClinico.Examenes, order: 4 },
      ],
      idAgendamiento
    );
  }

  private obtenerReportesDocumentosConsulta(idAgendamiento: string): Array<{
    nombre: TipoReporteResumenClinico;
    blob: Observable<Blob>;
    order: number;
  }> {
    return this.configurarReportes(
      [
        { nombre: TipoReporteResumenClinico.RecetaMedica, order: 1 },
        { nombre: TipoReporteResumenClinico.Terapias, order: 2 },
        { nombre: TipoReporteResumenClinico.Examenes, order: 3 },
      ],
      idAgendamiento
    );
  }

  private obtenerReportePorNombre(
    nombre: TipoReporteResumenClinico,
    idAgendamiento: string
  ): Array<{
    nombre: TipoReporteResumenClinico;
    blob: Observable<Blob>;
    order: number;
  }> {
    return this.configurarReportes([{ nombre, order: 1 }], idAgendamiento);
  }

  private configurarReportes(
    configuraciones: Array<{ nombre: TipoReporteResumenClinico; order: number }>,
    idAgendamiento: string
  ): Array<{
    nombre: TipoReporteResumenClinico;
    blob: Observable<Blob>;
    order: number;
  }> {
    return configuraciones.map(({ nombre, order }) => ({
      nombre,
      blob: this._reportesService.obtenerResumenClinicoPorNombre(nombre, idAgendamiento),
      order,
    }));
  }
}
